import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, ProductColor, ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Star, Eye, X, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useProducts, useCategories, useBrands } from '@/hooks/useSupabaseData';
import { useAdminProducts, useAdminBrands } from '@/hooks/useAdminSupabase';

export default function AdminProducts() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Supabase hooks
  const { products: supabaseProducts, loading: productsLoading, refreshProducts } = useProducts();
  const { categories, loading: categoriesLoading } = useCategories();
  const { brands: supabaseBrands, loading: brandsLoading } = useBrands();
  const { addProduct, updateProduct, deleteProduct, uploadImage, uploading } = useAdminProducts();
  const { addBrand: addBrandToSupabase, deleteBrand: deleteBrandFromSupabase } = useAdminBrands();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBrandDialogOpen, setIsBrandDialogOpen] = useState(false);
  const [newBrand, setNewBrand] = useState('');
  const [newBrandAr, setNewBrandAr] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // التحقق من تسجيل الدخول
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate(ROUTE_PATHS.ADMIN_LOGIN);
    }
  }, [navigate]);

  // تحديث المنتجات والماركات من Supabase
  useEffect(() => {
    setProducts(supabaseProducts);
  }, [supabaseProducts]);

  useEffect(() => {
    setBrands(supabaseBrands);
  }, [supabaseBrands]);

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminUsername');
    toast({
      title: 'تم تسجيل الخروج',
      description: 'تم تسجيل خروجك بنجاح',
    });
    navigate(ROUTE_PATHS.ADMIN_LOGIN);
  };
  
  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    price: undefined,
    originalPrice: undefined,
    description: '',
    image: '',
    images: [],
    category: categories[0] || 'مستلزمات البيبي',
    stock: 0,
    featured: false,
    brand: brands[0] || '',
    rating: 5,
    reviewCount: 0,
    views: 0,
    colors: [],
  });

  const [colorForm, setColorForm] = useState<ProductColor>({
    name: '',
    image: '',
  });

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData(product);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        price: undefined,
        originalPrice: undefined,
        description: '',
        image: '',
        images: [],
        category: categories[0] || 'مستلزمات البيبي',
        stock: 0,
        featured: false,
        brand: brands[0] || '',
        rating: 5,
        reviewCount: 0,
        views: 0,
        colors: [],
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
    setColorForm({ name: '', image: '' });
  };

  // إدارة الماركات
  const handleAddBrand = async () => {
    if (newBrand.trim() && newBrandAr.trim() && !brands.includes(newBrand.trim())) {
      const result = await addBrandToSupabase(newBrand.trim(), newBrandAr.trim());
      
      if (result.success) {
        setBrands([...brands, newBrand.trim()]);
        setNewBrand('');
        setNewBrandAr('');
        toast({
          title: 'تمت الإضافة',
          description: `تمت إضافة الماركة "${newBrandAr.trim()}" بنجاح`,
        });
        setIsBrandDialogOpen(false);
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في إضافة الماركة',
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: 'خطأ',
        description: 'الرجاء ملء جميع الحقول',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteBrand = async (brand: string) => {
    if (brands.length > 1) {
      const result = await deleteBrandFromSupabase(brand);
      
      if (result.success) {
        setBrands(brands.filter(b => b !== brand));
        toast({
          title: 'تم الحذف',
          description: `تم حذف الماركة "${brand}" بنجاح`,
        });
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في حذف الماركة',
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: 'خطأ',
        description: 'يجب أن يكون هناك ماركة واحدة على الأقل',
        variant: 'destructive',
      });
    }
  };

  const handleAddColor = () => {
    if (colorForm.name && colorForm.image) {
      setFormData({
        ...formData,
        colors: [...(formData.colors || []), colorForm],
      });
      setColorForm({ name: '', image: '' });
      toast({
        title: 'تمت الإضافة',
        description: 'تم إضافة اللون بنجاح',
      });
    }
  };

  const handleRemoveColor = (index: number) => {
    const newColors = [...(formData.colors || [])];
    newColors.splice(index, 1);
    setFormData({ ...formData, colors: newColors });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, isColor: boolean = false) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await uploadImage(file, isColor ? 'colors' : 'products');
      
      if (result.success && result.url) {
        if (isColor) {
          setColorForm({ ...colorForm, image: result.url });
        } else {
          setFormData({ ...formData, image: result.url });
        }
        toast({
          title: 'تم رفع الصورة',
          description: 'تم رفع الصورة بنجاح إلى Supabase Storage',
        });
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في رفع الصورة',
          variant: 'destructive',
        });
      }
    }
  };

  const handleMultipleImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newImages: string[] = [];
      
      for (const file of Array.from(files)) {
        const result = await uploadImage(file, 'products');
        if (result.success && result.url) {
          newImages.push(result.url);
        }
      }

      if (newImages.length > 0) {
        setFormData({
          ...formData,
          images: [...(formData.images || []), ...newImages],
        });
        toast({
          title: 'تم رفع الصور',
          description: `تم رفع ${newImages.length} صورة بنجاح إلى Supabase Storage`,
        });
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...(formData.images || [])];
    newImages.splice(index, 1);
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.image || !formData.price || formData.price <= 0) {
      toast({
        title: 'خطأ',
        description: 'يرجى ملء جميع الحقول المطلوبة (الاسم، الصورة، السعر)',
        variant: 'destructive',
      });
      return;
    }

    // تحضير البيانات للإرسال إلى Supabase
    const productData = {
      name: formData.name || '',
      name_ar: formData.name || '',
      description: formData.description || '',
      description_ar: formData.description || '',
      price: formData.price || 0,
      original_price: formData.originalPrice,
      category: formData.category || categories[0],
      brand: formData.brand || brands[0],
      image: formData.image || '',
      images: formData.images || [],
      stock: formData.stock || 0,
      rating: formData.rating || 0,
      review_count: formData.reviewCount || 0,
      views: formData.views || 0,
      featured: formData.featured || false,
    };

    if (editingProduct) {
      const result = await updateProduct(editingProduct.id, productData);
      
      if (result.success) {
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث المنتج بنجاح',
        });
        refreshProducts();
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في تحديث المنتج',
          variant: 'destructive',
        });
        return;
      }
    } else {
      const result = await addProduct(productData);
      
      if (result.success) {
        toast({
          title: 'تمت الإضافة',
          description: 'تم إضافة المنتج بنجاح',
        });
        refreshProducts();
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في إضافة المنتج',
          variant: 'destructive',
        });
        return;
      }
    }

    handleCloseDialog();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      const result = await deleteProduct(id);
      
      if (result.success) {
        toast({
          title: 'تم الحذف',
          description: 'تم حذف المنتج بنجاح',
        });
        refreshProducts();
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في حذف المنتج',
          variant: 'destructive',
        });
      }
    }
  };

  const getCategoryName = (categoryId: string) => {
    return categoryId; // الفئات الآن بالعربي مباشرة
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-primary text-primary'
                : 'fill-muted text-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-8" dir="rtl">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">إدارة المنتجات</h1>
              <p className="text-muted-foreground">إضافة وتعديل وحذف المنتجات</p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 ml-2" />
                تسجيل الخروج
              </Button>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 ml-2" />
                إضافة منتج جديد
              </Button>
            </div>
          </div>

          {/* Dialog للإضافة/التعديل */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}
                </DialogTitle>
                <DialogDescription>
                  {editingProduct
                    ? 'قم بتحديث معلومات المنتج'
                    : 'املأ التفاصيل لإضافة منتج جديد'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                  {/* اسم المنتج */}
                  <div className="space-y-2">
                    <Label htmlFor="name">اسم المنتج *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="مثال: عربة أطفال فاخرة"
                      required
                    />
                  </div>

                  {/* الصورة الرئيسية */}
                  <div className="space-y-2">
                    <Label htmlFor="image">الصورة الرئيسية *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, false)}
                        className="flex-1"
                      />
                      {formData.image && (
                        <img
                          src={formData.image}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded border"
                        />
                      )}
                    </div>
                  </div>

                  {/* معرض الصور الإضافية */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="images">صور إضافية للمنتج</Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      يمكنك رفع عدة صور للمنتج (الصورة الأولى ستكون الرئيسية في المعرض)
                    </p>
                    <Input
                      id="images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleMultipleImagesUpload}
                    />
                    
                    {/* عرض الصور المرفوعة */}
                    {formData.images && formData.images.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-4">
                        {formData.images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={img}
                              alt={`صورة ${index + 1}`}
                              className="w-full h-24 object-cover rounded border"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleRemoveImage(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            {index === 0 && (
                              <Badge className="absolute bottom-1 left-1 text-xs">
                                رئيسية
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* الأسعار */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="originalPrice">السعر الأصلي (ج.م)</Label>
                      <Input
                        id="originalPrice"
                        type="number"
                        value={formData.originalPrice || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, originalPrice: parseFloat(e.target.value) || undefined })
                        }
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      <p className="text-xs text-muted-foreground">
                        السعر قبل الخصم (اختياري)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">السعر بعد الخصم (ج.م) *</Label>
                      <Input
                        id="price"
                        type="number"
                        value={formData.price || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, price: parseFloat(e.target.value) || undefined })
                        }
                        placeholder="0"
                        min="0"
                        step="0.01"
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        السعر الحالي للبيع
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stock">الكمية المتاحة *</Label>
                      <Input
                        id="stock"
                        type="number"
                        value={formData.stock}
                        onChange={(e) =>
                          setFormData({ ...formData, stock: parseInt(e.target.value) })
                        }
                        placeholder="0"
                        min="0"
                        required
                      />
                    </div>
                  </div>

                  {/* الفئة والماركة */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">الفئة *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) =>
                          setFormData({ ...formData, category: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="brand">الماركة *</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsBrandDialogOpen(true)}
                        >
                          <Plus className="w-4 h-4 ml-1" />
                          إدارة الماركات
                        </Button>
                      </div>
                      <Select
                        value={formData.brand}
                        onValueChange={(value) =>
                          setFormData({ ...formData, brand: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {brands.map((brand) => (
                            <SelectItem key={brand} value={brand}>
                              {brand}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* الوصف */}
                  <div className="space-y-2">
                    <Label htmlFor="description">الوصف *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="وصف تفصيلي للمنتج..."
                      rows={4}
                      required
                    />
                  </div>

                  {/* التقييم والمشاهدات */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="rating">التقييم (1-5)</Label>
                      <Input
                        id="rating"
                        type="number"
                        value={formData.rating}
                        onChange={(e) =>
                          setFormData({ ...formData, rating: parseFloat(e.target.value) })
                        }
                        min="1"
                        max="5"
                        step="0.1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reviewCount">عدد التقييمات</Label>
                      <Input
                        id="reviewCount"
                        type="number"
                        value={formData.reviewCount}
                        onChange={(e) =>
                          setFormData({ ...formData, reviewCount: parseInt(e.target.value) })
                        }
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="views">عدد المشاهدات</Label>
                      <Input
                        id="views"
                        type="number"
                        value={formData.views}
                        onChange={(e) =>
                          setFormData({ ...formData, views: parseInt(e.target.value) })
                        }
                        min="0"
                      />
                    </div>
                  </div>

                  {/* منتج مميز */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label htmlFor="featured">منتج مميز</Label>
                      <p className="text-sm text-muted-foreground">
                        سيظهر في الصفحة الرئيسية
                      </p>
                    </div>
                    <Switch
                      id="featured"
                      checked={formData.featured}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, featured: checked })
                      }
                    />
                  </div>

                  {/* الألوان المتاحة */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <Label>الألوان المتاحة</Label>
                    
                    {/* قائمة الألوان الحالية */}
                    {formData.colors && formData.colors.length > 0 && (
                      <div className="space-y-2">
                        {formData.colors.map((color, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 border rounded"
                          >
                            <img
                              src={color.image}
                              alt={color.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <span className="flex-1">{color.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveColor(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* إضافة لون جديد */}
                    <div className="space-y-2">
                      <Input
                        placeholder="اسم اللون (مثال: أحمر، أزرق)"
                        value={colorForm.name}
                        onChange={(e) =>
                          setColorForm({ ...colorForm, name: e.target.value })
                        }
                      />
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, true)}
                          className="flex-1"
                        />
                        {colorForm.image && (
                          <img
                            src={colorForm.image}
                            alt="Preview"
                            className="w-12 h-12 object-cover rounded border"
                          />
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddColor}
                        disabled={!colorForm.name || !colorForm.image}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 ml-2" />
                        إضافة اللون
                      </Button>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={handleCloseDialog}>
                      إلغاء
                    </Button>
                    <Button type="submit">
                      {editingProduct ? 'تحديث المنتج' : 'إضافة المنتج'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

          {/* جدول المنتجات */}
          <div className="bg-card rounded-lg border overflow-x-auto" dir="rtl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الصورة</TableHead>
                  <TableHead className="text-right">المنتج</TableHead>
                  <TableHead className="text-right">الماركة</TableHead>
                  <TableHead className="text-right">الفئة</TableHead>
                  <TableHead className="text-right">السعر</TableHead>
                  <TableHead className="text-right">الكمية</TableHead>
                  <TableHead className="text-right">التقييم</TableHead>
                  <TableHead className="text-right">المشاهدات</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-right">الإجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{product.name}</div>
                        {product.colors && product.colors.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {product.colors.length} لون متاح
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{product.brand}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {getCategoryName(product.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {product.price.toLocaleString('ar-EG')} ج.م
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={product.stock > 10 ? 'default' : 'destructive'}
                      >
                        {product.stock}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {renderStars(product.rating)}
                        <span className="text-xs text-muted-foreground">
                          ({product.reviewCount})
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          {product.views.toLocaleString('ar-EG')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {product.featured && (
                        <Badge className="bg-accent text-accent-foreground">
                          <Star className="w-3 h-3 ml-1 fill-current" />
                          مميز
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* إحصائيات */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8" dir="rtl">
            <div className="p-6 bg-card rounded-lg border">
              <div className="text-sm text-muted-foreground mb-1">إجمالي المنتجات</div>
              <div className="text-3xl font-bold">{products.length}</div>
            </div>
            <div className="p-6 bg-card rounded-lg border">
              <div className="text-sm text-muted-foreground mb-1">المنتجات المميزة</div>
              <div className="text-3xl font-bold">
                {products.filter((p) => p.featured).length}
              </div>
            </div>
            <div className="p-6 bg-card rounded-lg border">
              <div className="text-sm text-muted-foreground mb-1">نفذت الكمية</div>
              <div className="text-3xl font-bold text-destructive">
                {products.filter((p) => p.stock === 0).length}
              </div>
            </div>
            <div className="p-6 bg-card rounded-lg border">
              <div className="text-sm text-muted-foreground mb-1">كمية محدودة</div>
              <div className="text-3xl font-bold text-amber-500">
                {products.filter((p) => p.stock > 0 && p.stock <= 10).length}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Dialog لإدارة الماركات */}
        <Dialog open={isBrandDialogOpen} onOpenChange={setIsBrandDialogOpen}>
          <DialogContent className="max-w-2xl" dir="rtl">
            <DialogHeader>
              <DialogTitle>إدارة الماركات</DialogTitle>
              <DialogDescription>
                إضافة أو حذف الماركات المتاحة
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* إضافة ماركة جديدة */}
              <div className="space-y-2">
                <Input
                  placeholder="اسم الماركة بالإنجليزي (مثل: Pampers)..."
                  value={newBrand}
                  onChange={(e) => setNewBrand(e.target.value)}
                />
                <Input
                  placeholder="اسم الماركة بالعربي (مثل: بامبرز)..."
                  value={newBrandAr}
                  onChange={(e) => setNewBrandAr(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddBrand();
                    }
                  }}
                />
                <Button onClick={handleAddBrand} className="w-full">
                  <Plus className="w-4 h-4 ml-1" />
                  إضافة ماركة
                </Button>
              </div>

              {/* قائمة الماركات */}
              <div className="border rounded-lg p-4 max-h-[400px] overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {brands.map((brand) => (
                    <div
                      key={brand}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <span className="font-medium">{brand}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteBrand(brand)}
                        className="h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                إجمالي الماركات: {brands.length}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsBrandDialogOpen(false)}>
                إغلاق
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}